import Phaser from 'phaser';

export class Alien extends Phaser.Physics.Arcade.Sprite {
  hp = 2;
  /** -1 / 1 — direction the alien is currently walking */
  walkDir: 1 | -1 = -1;
  private walkSpeed = 30;
  private deadFlag = false;

  constructor(scene: Phaser.Scene, x: number, y: number, tint?: number, textureKey = 'alien') {
    super(scene, x, y, textureKey);
    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.setOrigin(0.5, 1);
    if (tint !== undefined) this.setTint(tint);
    const body = this.body as Phaser.Physics.Arcade.Body;
    // Hitbox is intentionally TALLER than the visible sprite — the alien is
    // only 14px tall but the bullet line from a standing dino sits about 18px
    // off the ground, which is just above the visible sprite's head. We
    // extend the hitbox upward so any shot that *looks* like it should
    // connect actually does. Standard "fudge in the player's favor" trick.
    body.setSize(14, 24);
    body.setOffset(1, -10);
    body.setCollideWorldBounds(false);

    // Bob up and down a little for life.
    scene.tweens.add({
      targets: this,
      y: { from: y, to: y - 1 },
      duration: 380,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  isDead() {
    return this.deadFlag;
  }

  takeKick(damage: number, fromX: number) {
    if (this.deadFlag) return;
    this.hp -= damage;
    const dir = this.x < fromX ? -1 : 1;
    if (this.hp <= 0) {
      this.deadFlag = true;
      const body = this.body as Phaser.Physics.Arcade.Body;
      body.checkCollision.none = true;
      this.setVelocity(dir * 200, -260);
      this.scene.tweens.add({
        targets: this,
        angle: 720,
        alpha: 0,
        duration: 700,
        onComplete: () => this.destroy(),
      });
    } else {
      // Knockback only.
      this.setVelocity(dir * 120, -120);
      this.scene.tweens.add({
        targets: this,
        alpha: { from: 0.3, to: 1 },
        duration: 80,
        yoyo: true,
        repeat: 2,
      });
    }
  }

  /** Bullet hit — instant kill regardless of remaining HP. */
  killByBullet(fromX: number) {
    if (this.deadFlag) return;
    this.hp = 0;
    this.takeKick(99, fromX);
  }

  preUpdate(t: number, dt: number) {
    super.preUpdate(t, dt);
    if (this.deadFlag) return;
    const body = this.body as Phaser.Physics.Arcade.Body;

    // Wall-flip — if we're pressed against something solid, turn around so we
    // don't grind forever into a sky-tower base, ledge edge, or seam between
    // two ground tiles. Tiny `stuckTimer` covers the case where blocked.* is
    // intermittent (Phaser only reports it every other frame on a static body
    // contact) — if our X movement has been ~0 for a few frames while we're
    // trying to walk, flip anyway.
    if (
      (this.walkDir < 0 && body.blocked.left) ||
      (this.walkDir > 0 && body.blocked.right)
    ) {
      this.walkDir = (this.walkDir * -1) as 1 | -1;
      this.stuckTimer = 0;
    } else if (Math.abs(body.deltaX()) < 0.1 && body.onFloor()) {
      this.stuckTimer += dt;
      if (this.stuckTimer > 200) {
        this.walkDir = (this.walkDir * -1) as 1 | -1;
        this.stuckTimer = 0;
      }
    } else {
      this.stuckTimer = 0;
    }

    this.setVelocityX(this.walkDir * this.walkSpeed);
    this.setFlipX(this.walkDir > 0);
  }

  private stuckTimer = 0;
}
